/**
 * Mediciones longitudinales: baseline (W0) y checkpoints W4 / W8 / W12.
 *
 * Funciones PURAS. Reciben sesiones y grabaciones y devuelven el estado; no
 * leen de ningún sitio.
 *
 * Un checkpoint ES una sesión, no una entidad paralela. Todo lo que aquí se
 * calcula sale de `sesiones` con `tipo === 'checkpoint'` y su `checkpointSemana`.
 * No hay tabla de checkpoints y no debe haberla: sería una segunda fuente de
 * verdad que algún día diría otra cosa que las sesiones.
 */

import type { Grabacion } from '../modelo/grabacion.js';
import { cuentaComoHecha, type SesionRegistrada } from '../modelo/sesion.js';
import type { Area } from '../modelo/areas.js';

/** Las cuatro mediciones del programa inicial, en orden. W0 es el baseline. */
export const SEMANAS_MEDICION = [0, 4, 8, 12] as const;
export type SemanaMedicion = (typeof SEMANAS_MEDICION)[number];

export const ETIQUETA_MEDICION: Record<SemanaMedicion, string> = {
  0: 'Baseline W0',
  4: 'Checkpoint W4',
  8: 'Checkpoint W8',
  12: 'Checkpoint W12',
};

/**
 * La sesión que representa cada medición, o `null` si todavía no se hizo.
 *
 * W0 es el baseline COMPLETADO; un baseline parcial no mide nada. Los
 * checkpoints aceptan `parcial` porque miden varias cosas y perder una no
 * invalida las demás: la comparación muestra lo que haya y calla lo que no.
 */
export function medicionDeSemana(
  sesiones: SesionRegistrada[],
  semana: SemanaMedicion,
): SesionRegistrada | null {
  const candidatas =
    semana === 0
      ? sesiones.filter(
          (s) => s.tipo === 'baseline' && !s.deleted && s.estado === 'completada',
        )
      : sesiones.filter(
          (s) =>
            s.tipo === 'checkpoint' &&
            s.checkpointSemana === semana &&
            cuentaComoHecha(s),
        );

  if (candidatas.length === 0) return null;
  // Si se rehizo, vale la más reciente.
  return candidatas.reduce((a, b) => (a.iniciadaEn >= b.iniciadaEn ? a : b));
}

/** ¿Terminó el programa inicial de 12 semanas? */
export function programaInicialCompletado(sesiones: SesionRegistrada[]): boolean {
  return medicionDeSemana(sesiones, 12) !== null;
}

// ── Fuente de listening: nunca repetir un recurso ya consumido ─────────────

export interface EleccionDeFuente {
  /** La fuente asignada, o `null` si ya se usaron todas. */
  clave: string | null;
  /** Fuentes consumidas en mediciones anteriores. */
  usadas: string[];
  /**
   * `true` cuando no queda ninguna sin usar.
   *
   * NO es un error: es un estado legítimo del que hay que informar. La regla
   * es «no repetir SI hay alternativas», no «fallar si no las hay».
   */
  agotadas: boolean;
}

/**
 * Qué fuentes de listening se consumieron ya en mediciones.
 *
 * Solo cuentan baseline y checkpoints: el Día 5 con foco de listening usa
 * YouGlish y no gasta muestras auténticas.
 */
export function fuentesListeningUsadas(sesiones: SesionRegistrada[]): string[] {
  return sesiones
    .filter(
      (s) =>
        !s.deleted &&
        (s.tipo === 'baseline' || s.tipo === 'checkpoint') &&
        cuentaComoHecha(s) &&
        typeof s.fuenteListeningClave === 'string',
    )
    .sort((a, b) => (a.iniciadaEn < b.iniciadaEn ? -1 : 1))
    .map((s) => s.fuenteListeningClave!);
}

/**
 * Elegir una fuente que no se haya usado en una medición anterior.
 *
 * Si sabés de antemano qué vas a oír, la tarea deja de medir comprensión. Con
 * W0/W4/W8/W12 y cinco fuentes disponibles siempre queda alguna, pero el caso
 * de agotarlas se contempla igual: cuando pasa, se dice y se deja seguir a
 * mano. Nunca un bucle, nunca un fallo silencioso.
 *
 * Determinista: con las mismas sesiones devuelve siempre la misma fuente, o
 * la explicación de una medición dejaría de ser reproducible.
 */
export function elegirFuenteListening(
  sesiones: SesionRegistrada[],
  disponibles: readonly string[],
): EleccionDeFuente {
  const usadas = fuentesListeningUsadas(sesiones);
  const yaVistas = new Set(usadas);
  const libre = disponibles.find((clave) => !yaVistas.has(clave));
  return {
    clave: libre ?? null,
    usadas,
    agotadas: libre === undefined,
  };
}

export const AVISO_FUENTES_AGOTADAS =
  'Ya usaste todas las fuentes sugeridas en mediciones anteriores. Elegí vos un fragmento nuevo que no hayas escuchado: lo que hace válida la medición es que sea desconocido, no de qué sitio venga.';

// ── Comparación longitudinal ───────────────────────────────────────────────

export interface ColumnaMedicion {
  semana: SemanaMedicion;
  etiqueta: string;
  /** `null` cuando la medición todavía no se hizo: se muestra «Pendiente». */
  sesion: SesionRegistrada | null;
  /** Autoevaluaciones registradas en esa medición. Solo las que existen. */
  autoevaluaciones: Partial<Record<Area, number>>;
  confianza: number | null;
  /** Grabaciones de la medición, para poder escuchar la evolución. */
  grabaciones: Grabacion[];
  resumenChatGPT: string | null;
  fuenteListeningClave: string | null;
}

/**
 * Las cuatro columnas de la comparación.
 *
 * Una medición que falta se representa con `sesion: null`, NUNCA con ceros: un
 * cero dice «midió cero», que es lo contrario de «todavía no se midió».
 */
export function comparacionLongitudinal(
  sesiones: SesionRegistrada[],
  grabaciones: Grabacion[],
): ColumnaMedicion[] {
  return SEMANAS_MEDICION.map((semana) => {
    const sesion = medicionDeSemana(sesiones, semana);
    return {
      semana,
      etiqueta: ETIQUETA_MEDICION[semana],
      sesion,
      autoevaluaciones: sesion?.autoevaluaciones ?? {},
      confianza: sesion?.confianza ?? null,
      grabaciones: sesion
        ? grabaciones.filter((g) => !g.deleted && g.sesionId === sesion.id)
        : [],
      resumenChatGPT: sesion?.resumenChatGPT?.trim() || null,
      fuenteListeningClave: sesion?.fuenteListeningClave ?? null,
    };
  });
}

/** Áreas que tienen dato en al menos dos mediciones: las únicas comparables. */
export function areasComparables(columnas: ColumnaMedicion[], areas: readonly Area[]): Area[] {
  return areas.filter(
    (area) =>
      columnas.filter((columna) => typeof columna.autoevaluaciones[area] === 'number')
        .length >= 2,
  );
}
